import type { Commands } from '@tiptap/core'
import type { Slice } from '@tiptap/pm/model'
import type { EditorView } from '@tiptap/pm/view'
import { toast } from '@opallabs/lapidary'
import {
  UploadFailedToast,
  UploadProgressToast,
  UploadInvalidToast,
} from '../../toasts'
import type { TextEditorProps } from '../../types'
import type { AssetAttributes } from './types'

type HandlerType = (
  uploadAssets: TextEditorProps['onUploadAssets'],
  insertAsset: Commands['asset']['insertAsset']
) => <TEvent extends DragEvent | ClipboardEvent>(
  view: EditorView,
  event: TEvent,
  slice: Slice,
  moved?: TEvent extends DragEvent ? boolean : never
) => boolean

/**
 * ProseMirror hook to upload assets inserted via drop & paste events
 * onto the editor.
 */
export const createInsertHandler: HandlerType =
  (uploadAssets, insertAsset) => (view, event, slice, moved) => {
    const isDrop = event instanceof DragEvent
    const dataTransfer = isDrop ? event.dataTransfer : event.clipboardData
    const isValid =
      dataTransfer != null &&
      dataTransfer.files.length > 0 &&
      (!isDrop || !moved)

    // Detect if an external file has been dropped/pasted
    // Ignore other events
    if (isValid && typeof uploadAssets === 'function') {
      const files = Array.from(dataTransfer.files)

      // Exit early if no valid files were dropped
      if (!files.length) {
        toast(UploadInvalidToast, {
          type: 'error',
          toastId: 'asset-invalid-toast',
          closeOnClick: true,
          autoClose: 5000,
        })

        return false
      }

      const toastId = toast.loading(UploadProgressToast, {
        type: 'success',
        toastId: 'asset-upload-toast',
        closeOnClick: true,
        isLoading: true,
        autoClose: false,
      })

      uploadAssets(files).then(async (assets) => {
        const validAssets = assets.filter(
          (asset): asset is AssetAttributes => asset?.assetUuid != null
        )

        for (const asset of validAssets) {
          // Wait for next tick so the asset store gets updated
          await new Promise((res) => setTimeout(res))
          insertAsset(asset)
        }

        if (validAssets.length !== assets.length) {
          toast.update(toastId, {
            render: (props) => (
              <UploadFailedToast
                {...props}
                invalid={assets.length - validAssets.length}
                total={assets.length}
              />
            ),
            type: 'error',
            closeOnClick: true,
            autoClose: 5000,
          })
        } else {
          toast.dismiss(toastId)
        }
      })

      return true
    }

    return false
  }
